import { PriceChange, TokenBalanceResponse, TokenDataResponse, SymbolInfoResponse, TokenAvailabilityResponse, Ticker24hResponse } from '@crypto-trading/common/browser';
import { ApiClientManager } from '@/utils/api-client-factory';
import {
  FUTURES_PRICE_PRIORITY,
  getPriceMarkets,
  getPriceServiceRest,
  toFuturesServiceExchange,
  toServiceSymbol,
  type FuturesServiceExchange,
} from '@/lib/prices';

/** Price service futures venue -> the exchange codes trading screens use (live and demo). */
const TRADING_CODES: Record<FuturesServiceExchange, string[]> = {
  binance_futures: ['binance', 'binance-demo'],
  bybit_futures: ['bybit', 'bybit-demo'],
  gate_futures: ['gateio', 'gateio-demo'],
  kucoin_futures: ['kucoin', 'kucoin-demo'],
  aster_futures: ['aster', 'aster-demo'],
  hyperliquid: ['hyperliquid', 'hyperliquid-demo'],
};

/**
 * Market data API functions
 */

export const getPriceChanges = async (
  userId: string,
  symbol: string,
  timestamp: number,
  interval: '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' = '1m',
  limit: number = 15
): Promise<PriceChange[]> => {
  return ApiClientManager.getMarketDataClient().getPriceChanges(userId, symbol, timestamp, interval, limit);
};

export const getPriceChangesForPeriod = async (
  userId: string,
  symbol: string,
  startTime: number,
  endTime: number,
  interval: '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' = '1m',
  padding: number = 10
): Promise<PriceChange[]> => {
  return ApiClientManager.getMarketDataClient().getPriceChangesForPeriod(userId, symbol, startTime, endTime, interval, padding);
};

/**
 * A token's price on one exchange (mark, else last) from the price service
 * (prices.trigger.fun), not the api. No price there means no price.
 */
export const getTokenData = async (
  _userId: string,
  symbol: string,
  exchangeCode?: string
): Promise<TokenDataResponse | null> => {
  const exchange = toFuturesServiceExchange(exchangeCode ?? '');
  if (!exchange) throw new Error(`Unsupported exchange: ${exchangeCode}`);
  const serviceSymbol = toServiceSymbol(symbol);
  const rest = getPriceServiceRest();
  const [prices, hours] = await Promise.all([
    rest.getPrices([{ exchange, symbol: serviceSymbol }]),
    // 24h high, low and volume from the last 24 hourly candles.
    rest
      .getCandles({ exchange, symbol: serviceSymbol, interval: '1h', limit: 24 })
      .catch(() => []),
  ]);
  const price = prices[0];
  if (!price) {
    throw new Error(`Token ${serviceSymbol} not available on ${exchangeCode}`);
  }
  return {
    symbol: serviceSymbol,
    lastPrice: price.markPrice,
    highPrice: hours.length ? Math.max(...hours.map((c) => c.high)) : price.markPrice,
    lowPrice: hours.length ? Math.min(...hours.map((c) => c.low)) : price.markPrice,
    volume: hours.reduce((sum, c) => sum + c.volume, 0),
  };
};

export const get24hTicker = async (
  userId: string,
  symbol: string,
  exchangeCode?: string
): Promise<Ticker24hResponse | null> => {
  return ApiClientManager.getMarketDataClient().get24hTicker(userId, symbol, exchangeCode);
};

export const getTokenBalance = async (
  userId: string,
  symbol: string
): Promise<TokenBalanceResponse | null> => {
  return ApiClientManager.getMarketDataClient().getTokenBalance(userId, symbol);
};

export const getSymbolInfo = async (
  userId: string,
  symbol: string,
  exchangeCode?: string
): Promise<SymbolInfoResponse | null> => {
  return ApiClientManager.getMarketDataClient().getSymbolInfo(userId, symbol, exchangeCode);
};

/**
 * Exchanges where a token can be traded: the futures venues where the price
 * service lists and prices it (prices.trigger.fun /v1/markets), in the app's
 * usual order, so the api does not have to ask every exchange. A listed
 * venue without a price yet is left out until it has one.
 */
export const getTokenAvailability = async (
  _userId: string,
  symbol: string
): Promise<TokenAvailabilityResponse | null> => {
  const markets = await getPriceMarkets(symbol);
  const futures = new Set(markets.filter((m) => m.priced).map((m) => m.exchange));
  const userExchanges = FUTURES_PRICE_PRIORITY.filter((ex) => futures.has(ex)).flatMap(
    (ex) => TRADING_CODES[ex]
  );
  return { symbol: toServiceSymbol(symbol), userExchanges, allExchanges: [] };
};

